import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.jpeg';
import { HiMenu, HiX, HiUser, HiShoppingBag } from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { trackEvent } from '../utils/analytics';
import '../styles/Header.css';

const Header = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();

  /* `href` scrolls to a section on the home page; `to` is a real route. */
  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'Collection', href: '#collection' },
    { name: 'Blog', to: '/blog' },
    { name: 'About', href: '#about' },
    { name: 'Contact', href: '#footer' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    // Which parts of the site the header actually sends people to.
    trackEvent('nav_click', { nav_item: href.replace('#', '') });

    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // Section lives on the home page — go there first, then scroll.
    navigate('/', { state: { scrollTo: href } });
  };

  return (
    <motion.header
      className={`header ${scrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      style={{ width: '100%', height: 'auto' }}
    >
      <div className="header-content">
        <motion.div
          className="logo"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}>
          {/* Sized in Header.css — an inline width can't be overridden
              by the small-screen media query. */}
          <img src={logo} alt="Celestial Stones" className="logo-image" />
          <span>Celestial Stones</span>
        </motion.div>

        {/* Desktop Navigation */}
        <nav className="nav-desktop">
          <ul>
            {navItems.map((item, index) => (
              <motion.li
                key={item.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.3 }}
              >
                {item.to ? (
                  <Link to={item.to}>{item.name}</Link>
                ) : (
                  <a
                    href={item.href}
                    onClick={(e) => scrollToSection(e, item.href)}
                  >
                    {item.name}
                  </a>
                )}
              </motion.li>
            ))}
            <motion.li
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: navItems.length * 0.1 + 0.3 }}
            >
              <Link to={user ? '/account' : '/account/login'} className="nav-account">
                <HiUser />
                <span>{user ? 'My Account' : 'Sign In'}</span>
              </Link>
            </motion.li>
            <motion.li
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: navItems.length * 0.1 + 0.4 }}
            >
              <Link
                to="/cart"
                className="nav-cart"
                aria-label={`Cart, ${count} ${count === 1 ? 'item' : 'items'}`}
              >
                <HiShoppingBag />
                <span>Cart</span>
                {count > 0 && <span className="cart-badge">{count}</span>}
              </Link>
            </motion.li>
          </ul>
        </nav>

        {/* Cart stays reachable on mobile without opening the menu */}
        <Link
          to="/cart"
          className="header-cart-mobile"
          aria-label={`Cart, ${count} ${count === 1 ? 'item' : 'items'}`}
        >
          <HiShoppingBag />
          {count > 0 && <span className="cart-badge">{count}</span>}
        </Link>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <HiX /> : <HiMenu />}
        </button>

        {/* Tap outside to dismiss the mobile menu */}
        {mobileMenuOpen && (
          <div
            className="nav-mobile-backdrop"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.nav
              className="nav-mobile"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ul>
                {navItems.map((item) => (
                  <motion.li
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    {item.to ? (
                      <Link
                        to={item.to}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ) : (
                      <a
                        href={item.href}
                        onClick={(e) => scrollToSection(e, item.href)}
                      >
                        {item.name}
                      </a>
                    )}
                  </motion.li>
                ))}
                <motion.li
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <Link
                    to={user ? '/account' : '/account/login'}
                    className="nav-account"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <HiUser />
                    <span>{user ? 'My Account' : 'Sign In'}</span>
                  </Link>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <Link
                    to="/cart"
                    className="nav-cart"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <HiShoppingBag />
                    <span>Cart{count > 0 ? ` (${count})` : ''}</span>
                  </Link>
                </motion.li>
              </ul>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
